import React, { useState, useEffect } from 'react';
import { User, Users, Loader2, AlertTriangle } from 'lucide-react';
import { JerseyColor, Player } from '../../types/Player';
import { Coach } from '../../types/Coach';
import { EvaluationSession } from '../../hooks/useEvaluationSessionData';
import { PlayerEvaluation } from '../../types/playerEvaluation';
import EvaluationForm from './EvaluationForm';
import OtherEvaluationsList from './OtherEvaluationsList';
import JerseyUpdateModal from './JerseyUpdateModal';
import JerseyBadge from './JerseyBadge';
import { formatDateString } from '../../lib/utils';
import { findProperAgeGroup, formatAgeGroupLabel, AgeGroupWithRange } from '../../lib/ageGroups';

const isEvaluationComplete = (evaluation: PlayerEvaluation): boolean => {
  return !!evaluation.ratings && Object.keys(evaluation.ratings).length >= 2;
};

type EvaluationFormData = Partial<Omit<PlayerEvaluation, 'team_recommendations'> & { team_recommendations?: { [key: string]: boolean } }>;

interface SelectedPlayerCardProps {
  selectedPlayer: Player | null;
  evaluationId: string;
  coachId: string;
  coaches: Coach[];
  evaluation: EvaluationSession | null;
  allAgeGroups: AgeGroupWithRange[];
  myEvaluationFormData: EvaluationFormData;
  allPlayerEvaluations: PlayerEvaluation[] | null;
  isLoadingEvaluations: boolean;
  handleScoreChange: (category: string, value: number) => void;
  handleNotesChange: (notes: string) => void;
  handleTeamRecommendationChange: (teamLevel: string, selected: boolean) => void;
  saveStatus: "idle" | "saving" | "saved" | "error";
  isDirty: boolean;
  saveError: string | null;
  onUpdateJersey: (playerId: string, newJerseyNumber: string, newJerseyColor: JerseyColor) => Promise<void>;
}

const SelectedPlayerCard: React.FC<SelectedPlayerCardProps> = ({
  selectedPlayer,
  evaluationId,
  coachId,
  coaches,
  evaluation,
  allAgeGroups,
  myEvaluationFormData,
  allPlayerEvaluations,
  isLoadingEvaluations,
  handleScoreChange,
  handleNotesChange,
  handleTeamRecommendationChange,
  saveStatus,
  isDirty,
  saveError,
  onUpdateJersey,
}) => {
  if (!selectedPlayer) {
    return (
      <div className="text-center py-12 text-gray-500">
        <User className="h-12 w-12 mx-auto mb-4" />
        <p>Select a player to begin evaluation</p>
      </div>
    );
  }

  const ageGroupName = evaluation?.evaluation_session_age_groups?.[0]?.age_groups.group_name || '';
  const properAgeGroup = findProperAgeGroup(selectedPlayer.birthdate, selectedPlayer.sex, allAgeGroups);
  const properAgeGroupLabel = selectedPlayer.birthdate
    ? properAgeGroup
      ? formatAgeGroupLabel(properAgeGroup)
      : 'No matching age group'
    : '';
  const completedEvaluationsCount = allPlayerEvaluations
    ? allPlayerEvaluations.filter(isEvaluationComplete).length
    : 0;
  const otherEvaluations = allPlayerEvaluations
    ? allPlayerEvaluations.filter(e => e.coach_id !== coachId)
    : [];
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  useEffect(() => {
    if (saveStatus === "saved" && !saveError) {
      setLastSavedAt(new Date());
    }
  }, [saveStatus, saveError]);

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-semibold flex items-center">
          <User className="h-5 w-5 mr-2 text-blue-600" />
          Player Evaluation
        </h2>
        <div className="flex items-center space-x-2">
          <JerseyBadge number={selectedPlayer.jerseyNumber} color={selectedPlayer.jerseyColor} size="lg" />
          <button
            onClick={() => setIsUpdateModalOpen(true)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Edit
          </button>
        </div>
      </div>

      {saveError && (
        <div className="my-4 p-3 border border-red-300 bg-red-50 rounded-lg flex items-start text-red-700">
          <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
          <div>
            <p className="font-medium">Error Saving Evaluation</p>
            <p className="text-sm">{saveError}</p>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h3 className="font-medium text-lg">
          {(selectedPlayer.first_name ?? '') + ' ' + (selectedPlayer.last_name ?? '')}
        </h3>
        <p className="text-sm text-gray-600">
          {selectedPlayer.birthdate
            ? formatDateString(selectedPlayer.birthdate, 'en-US', 'medium')
            : ''}
          {properAgeGroupLabel ? ` (${properAgeGroupLabel})` : ''}
          {selectedPlayer.birthdate && (selectedPlayer.profile?.positions?.length ?? 0) > 0 ? ' • ' : ''}
          {(selectedPlayer.profile?.positions ?? []).join(', ')}
        </p>
        <p className="text-sm text-gray-600">
          Desired team(s): {(selectedPlayer.profile?.competitiveness?.length ?? 0) > 0 ? (selectedPlayer.profile?.competitiveness ?? []).join(', ') : 'Not Specified'}
        </p>
        {selectedPlayer.profile?.notes && (
          <p className="text-sm text-gray-600 mt-1">
            Parent Notes: <span className="italic">{selectedPlayer.profile.notes}</span>
          </p>
        )}
        <p className={`text-sm mt-1 ${saveStatus === 'error' ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
          Save Status: {
            saveStatus === 'saving' ? 'Saving...' :
            saveStatus === 'saved' ? `Saved ${lastSavedAt ? `at ${lastSavedAt.toLocaleTimeString()}` : ''}` :
            saveStatus === 'error' ? 'Error' :
            isDirty ? 'Unsaved changes' : `Saved ${lastSavedAt ? `at ${lastSavedAt.toLocaleTimeString()}` : ''}`
          }
        </p>
        <div className="mt-2 flex items-center space-x-2 h-5">
          {isLoadingEvaluations ? (
            <span className="text-sm text-gray-500 flex items-center"><Loader2 className="h-4 w-4 mr-1 animate-spin" />Loading evaluations...</span>
          ) : completedEvaluationsCount > 0 ? (
            <>
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-600">
                {completedEvaluationsCount} coach{completedEvaluationsCount !== 1 ? 'es' : ''} completed evaluation
              </span>
            </>
          ) : (
            allPlayerEvaluations && allPlayerEvaluations.length > 0 && <span className="text-sm text-gray-500">No complete evaluations yet.</span>
          )}
        </div>
      </div>

      <EvaluationForm
        myEvaluation={myEvaluationFormData as PlayerEvaluation}
        handleScoreChange={handleScoreChange}
        handleNotesChange={handleNotesChange}
        handleTeamRecommendationChange={handleTeamRecommendationChange}
      />

      {isLoadingEvaluations ? (
        <div className="border-t pt-6 mt-6 text-center text-gray-500">
          <Loader2 className="h-5 w-5 mx-auto animate-spin" />
          <p>Loading other evaluations...</p>
        </div>
      ) : (
        otherEvaluations.length > 0 && (
          <div className="mt-6">
            <OtherEvaluationsList
              playerEvaluations={otherEvaluations}
              coaches={coaches}
              ageGroupName={ageGroupName}
            />
          </div>
        )
      )}

      <JerseyUpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onUpdate={(newNumber, newColor) => onUpdateJersey(selectedPlayer.id, newNumber, newColor)}
        currentNumber={selectedPlayer.jerseyNumber || ''}
        currentColor={selectedPlayer.jerseyColor}
      />
    </>
  );
};

export default SelectedPlayerCard;